/* ===========================================================
   useSeo — head por rota (title, description, canonical, Open Graph,
   Twitter) para uma SPA sem SSR.

   Por que efeito e não <head> estático: o React 18 não faz hoist de
   <title>/<meta>, e o index.html só carrega o head da HOME. As páginas de
   conteúdo precisam do seu próprio head — este hook escreve nas tags
   existentes (ou cria) no mount. O prerender (Chrome real) roda o app,
   espera o seletor da rota e só então serializa o HTML, então as mutações
   de head entram no arquivo estático que o Google e os crawlers de IA leem.

   A HOME mantém o head rico do index.html (não chama este hook). /termos,
   /privacidade e todo o conteúdo novo passam a ter head próprio — antes
   herdavam o título da home (duplicata aos olhos do buscador).
   =========================================================== */
import { useEffect } from "react";
import { SITE, urlCanonica } from "./site";

export interface SeoMeta {
  /** <title> completo (já com " | Lucraa+" quando fizer sentido). */
  title: string;
  description: string;
  /** Caminho da rota (ex.: "/glossario/roas") → canonical + og:url. */
  path: string;
  /** "website" (padrão) ou "article" para posts do blog. */
  ogType?: "website" | "article";
}

function upsertMeta(attr: "name" | "property", key: string, content: string): void {
  const sel = `meta[${attr}="${key}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(sel);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function useSeo({ title, description, path, ogType = "website" }: SeoMeta): void {
  useEffect(() => {
    const url = urlCanonica(path);
    document.title = title;
    upsertCanonical(url);
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:type", ogType);
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:site", SITE.twitter);
    // Sinal para o prerender (SSG): só serializa a rota depois que o head
    // desta página foi aplicado. Sem isso, páginas lazy seriam capturadas
    // antes do efeito rodar e herdariam o <title> da home. Ver prerender.mjs.
    document.documentElement.setAttribute("data-seo", "ready");
  }, [title, description, path, ogType]);
}
